import React from 'react';
import { IconsEnum } from '../../../constants/icons';
import classes from './SettingsMenu.module.scss';
import { ListItem } from '../../../components/actions/ListItem';
import { WithChildren } from '../../../models/children-props';
import { ListSectionTitle } from '../../../components/actions/ListItem/ListSectionTitle';
import { UserAppRole, UserOrganizationRole } from '../../../models/graphql';
import { useLocation } from 'react-router-dom';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { ApolloError } from '@apollo/client';
import { useAuth } from '../../../hooks/useAuth';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import classNames from 'classnames';

export type SettingsMenuViewProps = WithChildren & {
  workspaceName: string;
  menuItemClickHandler: (url: string) => void;
  selectedOrgRole: UserOrganizationRole | null;
  appRole: UserAppRole;
};

const ExposedTeamOptions = (props: SettingsMenuViewProps): JSX.Element => {
  const location = useLocation();

  return (
    <>
      <ListSectionTitle type={'li'}>Account Settings</ListSectionTitle>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/account')}
        disabled={false}
        selected={location.pathname === '/settings/account'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.userProfile}>
        My Account
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/notifications')}
        disabled={false}
        selected={location.pathname === '/settings/notifications'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.bell}>
        Emails & Notifications
      </ListItem>
      {(props.selectedOrgRole === UserOrganizationRole.OWNER ||
        props.selectedOrgRole === UserOrganizationRole.ADMIN) && (
        <>
          <ListSectionTitle type={'li'}>Organization</ListSectionTitle>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/workspaces')}
            disabled={false}
            selected={location.pathname === '/settings/workspaces'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.preferences}>
            Workspaces
          </ListItem>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/team')}
            disabled={false}
            selected={location.pathname === '/settings/team'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.team}>
            Team Members
          </ListItem>
          {props.selectedOrgRole === UserOrganizationRole.OWNER && (
            <>
              <ListItem
                action={() => props.menuItemClickHandler('/settings/business-details')}
                disabled={false}
                selected={location.pathname === '/settings/business-details'}
                type={'li'}
                variation={'dark'}
                icon={IconsEnum.subscription}>
                Business Details
              </ListItem>
              <ListItem
                action={() => props.menuItemClickHandler('/settings/manage-subscription')}
                disabled={false}
                selected={location.pathname === '/settings/manage-subscription'}
                type={'li'}
                variation={'dark'}
                icon={IconsEnum.subscription}>
                Subscription Management
              </ListItem>
              <ListItem
                action={() => props.menuItemClickHandler('/settings/billings')}
                disabled={false}
                selected={location.pathname === '/settings/billings'}
                type={'li'}
                variation={'dark'}
                icon={IconsEnum.billing}>
                Billing
              </ListItem>
              <ListItem
                action={() => props.menuItemClickHandler('/settings/affiliate')}
                disabled={false}
                selected={location.pathname === '/settings/affiliate'}
                type={'li'}
                variation={'dark'}
                icon={IconsEnum.affiliate}>
                Affiliate Program
              </ListItem>
            </>
          )}
        </>
      )}
      <ListSectionTitle type={'li'}>Workspace / {props.workspaceName}</ListSectionTitle>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/profiles')}
        disabled={false}
        selected={location.pathname === '/settings/profiles'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.userProfile}>
        Social Profiles
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/publishing')}
        disabled={false}
        selected={location.pathname === '/settings/publishing'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.settings}>
        Publishing Settings
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/tracking')}
        disabled={false}
        selected={location.pathname === '/settings/tracking'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.link}>
        Link Tracking
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/pause')}
        disabled={false}
        selected={location.pathname === '/settings/pause'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.pause}>
        Pause Settings
      </ListItem>
      <ListSectionTitle type={'li'}>Tools</ListSectionTitle>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/extension')}
        disabled={false}
        selected={location.pathname === '/settings/extension'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.browser}>
        Browser Extension
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/mobile')}
        disabled={false}
        selected={location.pathname === '/settings/mobile'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.mobile}>
        Mobile App
      </ListItem>
      {props.appRole === UserAppRole.ADMIN && (
        <>
          <ListSectionTitle type={'li'}>Admin</ListSectionTitle>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/admin/users')}
            disabled={false}
            selected={location.pathname === '/settings/admin/users'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.team}>
            User List
          </ListItem>
        </>
      )}
    </>
  );
};

const HiddenTeamOptions = (props: SettingsMenuViewProps): JSX.Element => {
  const location = useLocation();

  return (
    <>
      <ListSectionTitle type={'li'}>Account Settings</ListSectionTitle>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/profiles')}
        disabled={false}
        selected={location.pathname === '/settings/profiles'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.userProfile}>
        Social Profiles
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/publishing')}
        disabled={false}
        selected={location.pathname === '/settings/publishing'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.settings}>
        Publishing Settings
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/tracking')}
        disabled={false}
        selected={location.pathname === '/settings/tracking'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.link}>
        Link Tracking
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/pause')}
        disabled={false}
        selected={location.pathname === '/settings/pause'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.pause}>
        Pause Settings
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/notifications')}
        disabled={false}
        selected={location.pathname === '/settings/notifications'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.bell}>
        Emails & Notifications
      </ListItem>
      {props.selectedOrgRole === UserOrganizationRole.OWNER && (
        <>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/billings')}
            disabled={false}
            selected={location.pathname === '/settings/billings'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.billing}>
            Billing
          </ListItem>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/business-details')}
            disabled={false}
            selected={location.pathname === '/settings/business-details'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.userProfile}>
            Business Details
          </ListItem>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/manage-subscription')}
            disabled={false}
            selected={location.pathname === '/settings/manage-subscription'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.userProfile}>
            Subscription Management
          </ListItem>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/affiliate')}
            disabled={false}
            selected={location.pathname === '/settings/affiliate'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.affiliate}>
            Affiliate Program
          </ListItem>
        </>
      )}
      <ListItem
        action={() => props.menuItemClickHandler('/settings/account')}
        disabled={false}
        selected={location.pathname === '/settings/account'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.userProfile}>
        My Account
      </ListItem>
      <ListSectionTitle type={'li'}>Tools</ListSectionTitle>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/extension')}
        disabled={false}
        selected={location.pathname === '/settings/extension'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.browser}>
        Browser Extension
      </ListItem>
      <ListItem
        action={() => props.menuItemClickHandler('/settings/mobile')}
        disabled={false}
        selected={location.pathname === '/settings/mobile'}
        type={'li'}
        variation={'dark'}
        icon={IconsEnum.mobile}>
        Mobile App
      </ListItem>
      {props.appRole === UserAppRole.ADMIN && (
        <>
          <ListSectionTitle type={'li'}>Admin</ListSectionTitle>
          <ListItem
            action={() => props.menuItemClickHandler('/settings/admin/users')}
            disabled={false}
            selected={location.pathname === '/settings/admin/users'}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.team}>
            User List
          </ListItem>
        </>
      )}
    </>
  );
};

export const SettingsMenuView = (props: SettingsMenuViewProps): JSX.Element => {
  const { logout } = useAuth();
  const { selectedOrganization } = useSelectedOrganization();
  const { isAccessBlocked } = useSubscriptionStatus();

  // TODO-Overhaul-Release - we'll need to expose these options for migrated members eventually.
  const exposeOrganizationSettings = selectedOrganization?.fromLegacy === false;

  let menuOptions = exposeOrganizationSettings ? <ExposedTeamOptions {...props} /> : <HiddenTeamOptions {...props} />;

  if (isAccessBlocked) {
    menuOptions = <></>;
  }

  return (
    <div className={classNames(classes.SettingsMenu, {
      [classes.SettingsMenuOnlyLogout]: isAccessBlocked
    })}>
      {props.children}
      <ul className={classNames(classes.SettingsMenuGroup, {
        [classes.SettingsMenuGroupOnlyLogout]: isAccessBlocked
      })}>{menuOptions}</ul>
      <div className={props.isLeftSideMenu ? classes.LogOutContainer : undefined}>
        <hr className={classes.Separator} />
        <ul className={classes.SettingsMenuGroup}>
          <ListItem
            action={async () => {
              logout().catch(error => {
                /* prevent runtime error */
                showToast({
                  severity: 'error',
                  summary: 'Logout Error',
                  detail: remapApolloErrors(error as ApolloError)
                });
              });
            }}
            disabled={false}
            selected={false}
            type={'li'}
            variation={'dark'}
            icon={IconsEnum.logout}>
            Log Out
          </ListItem>
        </ul>
      </div>
    </div>
  );
};
